import { Frame, NodeId, SerializedNode } from "@craftjs/core";
import isEmpty from "lodash/isEmpty";
import React, { PropsWithChildren, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";

import useTheme from "@/components/editor/themes/use-theme";
import serializePageContent from "@/utils/serialize-page-content";

const SkipNavLink = styled.a`
  position: absolute;
  top: 0;
  left: 0;
  z-index: 10;
  height: 1px;
  width: 1px;
  overflow: hidden;
  clip: rect(1px 1px 1px 1px);

  &:focus {
    height: auto;
    width: auto;
    clip: auto;
  }
`;

type FrameProps = Omit<React.ComponentProps<typeof Frame>, "data"> & {
  data?: Record<NodeId, SerializedNode>;
};

const EditorFrame = (props: PropsWithChildren<FrameProps>) => {
  const { t } = useTranslation();
  const { data, ...otherProps } = props;

  const { currentTheme } = useTheme();

  const initialDataRef = useRef(data);
  const initialThemeRef = useRef(currentTheme);

  const firstContentNode = useMemo(() => {
    const blocks = initialDataRef.current;
    if (isEmpty(blocks)) {
      return;
    }
    return blocks.ROOT.nodes.find((node) => {
      const nodeType = blocks[node].type;
      const componentName =
        typeof nodeType === "string" ? nodeType : nodeType.resolvedName;
      return componentName !== "StandaloneNavigation";
    });
  }, []);

  const validatedData = useMemo(
    () =>
      !isEmpty(initialDataRef.current)
        ? serializePageContent(
            initialDataRef.current,
            initialThemeRef.current.blocks
          )
        : null,
    []
  );

  return (
    <>
      {firstContentNode && (
        <SkipNavLink
          data-testid="skip-navigation-link"
          href={`#${firstContentNode}`}
        >
          {t("common.skip-navigation")}
        </SkipNavLink>
      )}
      <Frame data={validatedData} {...otherProps} />
    </>
  );
};

export default EditorFrame;
